import React, { useState, useEffect } from "react";
import { Control, Controller, UseFormReset, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { CustomFieldModel } from "src/api/contractGroup/contractgroup-type";
import { BaseButton, BaseInput, BaseSelect } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { FIELD_TYPE_OPTIONS } from "../../contractgroup-constant";

type Props = {
  control: Control<CustomFieldModel>;
  watch: UseFormWatch<CustomFieldModel>;
  setValue: UseFormSetValue<CustomFieldModel>;
  reset: UseFormReset<CustomFieldModel>;
  onClose: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

const CustomFieldFormModal = ({ control, watch, setValue, reset, onClose, onSubmit }: Props) => {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <BaseAbstractModal size="large" isOpen={true}>
      <form onSubmit={onSubmit}>
        <section className="base-abstract-modal__title flex-center-between border-bottom-gray mb20 ">
          <h1>커스텀 필드 설정</h1>
        </section>
        <div className="px30">
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p className="required">필드명</p>
            </div>
            <div className="contents-container__grid-contents minmax240">
              <Controller
                control={control}
                name="fieldName"
                rules={{ required: "필수입력 항목입니다." }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                  return <BaseInput onChange={onChange} value={value} name={name} placeholder="필드명을 입력해 주세요" errorText={error?.message} />;
                }}
              />
            </div>
          </section>
          <section className="contents-container__grid">
            <div className="contents-container__grid-index">
              <p className="required">필드 유형</p>
            </div>
            <div className="contents-container__grid-contents minmax240">
              <Controller
                control={control}
                name="fieldType"
                rules={{ required: "필수입력 항목입니다." }}
                render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                  return (
                    <BaseSelect
                      stateOptions={FIELD_TYPE_OPTIONS}
                      setStateValue={onChange}
                      value={value}
                      errorText={error?.message}
                      isDisabled={watch("fieldId") !== 0}
                    />
                  );
                }}
              />
            </div>
          </section>

          {(watch("fieldType") === "RADIO" || watch("fieldType") === "CHECKBOX") && (
            <div
              className="px20 py12" //
              style={{
                background: "#F8F8FA",
                borderRadius: "10px",
                overflowY: "auto",
                maxHeight: `min(825px, ${windowHeight * 0.4}px)`,
              }}
            >
              {watch("optionList")?.map((item, index) => (
                <Controller
                  key={item.orderNum}
                  control={control}
                  name={`optionList.${index}.optionName`}
                  rules={{
                    required: !item.isDeleted ? "필수입력 항목입니다." : false,
                  }}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                    return (
                      <>
                        {!item.isDeleted && (
                          <section className="contents-container__grid " key={item.orderNum}>
                            <div className="contents-container__grid-index">
                              <p className="required">
                                옵션
                                {(watch("optionList") || []).filter((item) => !item.isDeleted).findIndex((opt) => opt.orderNum === item.orderNum) + 1}
                              </p>
                            </div>
                            <div className="contents-container__grid-contents">
                              <div className=" flex-direction-start flex-row flex-center-start ">
                                <BaseInput
                                  name={name}
                                  className="minmax240"
                                  placeholder="옵션명을 입력해 주세요"
                                  value={value}
                                  onChange={onChange}
                                  // errorText={error?.message}
                                />
                                {(() => {
                                  const visibleOptions = watch("optionList")?.filter((item) => !item.isDeleted) || [];
                                  const isFirstItem = visibleOptions.findIndex((opt) => opt.orderNum === item.orderNum) === 0;
                                  const canDelete = visibleOptions.length > 1;

                                  return isFirstItem ? (
                                    <>
                                      <button
                                        className="minus-btn ml10"
                                        // disabled={!canDelete}
                                        disabled={!canDelete}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (!canDelete) return;

                                          setValue(
                                            "optionList",
                                            watch("optionList")!.map((item, i) => {
                                              if (i === index) {
                                                return { ...item, isDeleted: true };
                                              }
                                              return item;
                                            }),
                                          );
                                        }}
                                      />
                                      <button
                                        className="plus-btn ml10"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setValue("optionList", [
                                            ...(watch("optionList") || []),
                                            {
                                              optionId: 0,
                                              optionName: "",
                                              isDeleted: false,
                                              orderNum: Math.max(...(watch("optionList")?.map((item) => item.orderNum || 0) || [0])) + 1,
                                            },
                                          ]);
                                        }}
                                      />
                                    </>
                                  ) : (
                                    <button
                                      className="minus-btn ml10"
                                      disabled={!canDelete}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (!canDelete) return;

                                        const currentOptions = watch("optionList")!;
                                        const targetOption = currentOptions[index];

                                        if (targetOption.optionId && targetOption.optionId !== 0) {
                                          // 기존 데이터: isDeleted true로 설정
                                          setValue(
                                            "optionList",
                                            currentOptions.map((item, i) => {
                                              if (i === index) {
                                                return { ...item, isDeleted: true };
                                              }
                                              return item;
                                            }),
                                          );
                                        } else {
                                          // 새로 추가된 데이터: filter로 완전 삭제
                                          setValue(
                                            "optionList",
                                            currentOptions.filter((_, i) => i !== index),
                                          );
                                        }
                                      }}
                                    />
                                  );
                                })()}
                              </div>
                              {error?.message && <p className="validation-text">{error?.message}</p>}
                            </div>
                          </section>
                        )}
                      </>
                    );
                  }}
                />
              ))}
            </div>
          )}
        </div>
        <section className="base-abstract-modal__btn-wrap">
          <BaseButton
            className="color-white"
            title="취소"
            onClick={() => {
              onClose();
              reset();
            }}
          />
          <BaseButton title="저장" type="submit" />
        </section>
      </form>
    </BaseAbstractModal>
  );
};

export default CustomFieldFormModal;
